import axios from '../axios';

// ---------------------------------------------------------------------------
// Usuários (IAM) — Service Layer (handoff 0110, refactor Gen3 do backend)
//
// Camada única da fronteira (FC1 §A.7): desembrulha o envelope JSend no sucesso
// (`data.data`) e propaga o `ApiError` que o interceptor de contrato já
// normalizou — nenhum parsing de erro acontece aqui. A tradução camelCase (args
// do front) → snake_case (payload/query do backend) mora SÓ neste arquivo.
//
// O módulo é REST desde 2026-09-14: `GET/POST /users`, `GET/PUT/DELETE
// /users/:id`, `PUT /users/:id/pdvs`. Morreram `/users/show/:id`, `/users/new`,
// `DELETE /users` (que deletava o próprio logado) e `/users/search/name` — a
// busca virou o param `q` da listagem.
//
// RBAC por verbo: `iam:user:read` (listar/ver), `:create`, `:update` (edição E
// PDVs), `:delete`. As rotas `/users/me/*` não exigem permissão — são a conta
// de quem está logado.
// ---------------------------------------------------------------------------

// Toda MUTAÇÃO sai com `_skipForbiddenRedirect` (precedente de
// `inventory/transfersApi.js`). Um 403 aqui é `missing_permission`, e as telas
// já escondem o botão de quem não tem a chave — ou seja, quando ele acontece é
// por divergência de grant no meio da sessão. Nesse caso o redirect global é
// destrutivo: `window.location.href` recarrega a página e leva junto o
// formulário preenchido. Com a flag, o erro chega como `ApiError` no catch da
// tela e vira toast.
const ACTION_CONFIG = { _skipForbiddenRedirect: true };

// Meta de paginação do JSend — mesma leitura defensiva de `customersApi`.
function readPagination(envelope, fallbackPerPage = 20) {
   const meta = envelope?.meta ?? {};
   return {
      total: meta.total ?? 0,
      page: meta.page ?? 1,
      perPage: meta.per_page ?? fallbackPerPage,
      totalPages: meta.total_pages ?? 1,
   };
}

// ===========================================================================
// ADMINISTRAÇÃO DE CONTAS
// ===========================================================================

/**
 * Listagem paginada — tela de administração, `iam:user:read` (Master-only).
 * Seletor de usuário de outro módulo NÃO usa esta função: usa
 * `listUserDirectory` (abaixo).
 *
 * `status` é OBRIGATÓRIO no backend (ausente → 422); o default `active` mora
 * aqui, na margem, para que a tela não precise saber disso.
 *
 * `q` faz contains em nome OU email. Não há `order_by` no contrato: a
 * ordenação é a do backend, e a listagem não expõe cabeçalho clicável
 * (`navegacao.md` 1.10 — ordenação fantasma é pior que ausência).
 *
 * @param {object} filters
 * @param {string} [filters.q]
 * @param {'active'|'inactive'|'all'} [filters.status='active']
 * @param {number|string} [filters.roleId]
 * @param {number} [filters.page]
 * @param {number} [filters.perPage]
 * @param {object} [options]
 * @param {boolean} [options.skipForbiddenRedirect] ver nota abaixo
 * @returns {Promise<{users: Array, total: number, page: number, perPage: number, totalPages: number}>}
 */
export async function listUsers(filters = {}, options = {}) {
   const { q, status, roleId, page, perPage } = filters;

   const params = { status: status || 'active' };
   if (q) params.q = q;
   if (roleId) params.role_id = roleId;
   if (page) params.page = page;
   if (perPage) params.per_page = perPage;

   // A flag existe para quem lê esta listagem de DENTRO de outra tela — hoje a
   // aba "Usuários vinculados" da tela de perfil de acesso, que é gateada por
   // `iam:role:read` e não por `iam:user:read`. Nesse caso o 403 não significa
   // "esta tela não é sua", e o redirect global levaria junto o diff de
   // permissões em edição; quem passa a flag assume degradar na própria UI.
   // Os SELETORES de usuário de outros módulos não usam isto: eles vão pelo
   // `listUserDirectory`, que não exige permissão nenhuma (§5 do doc).
   const config = options.skipForbiddenRedirect
      ? { params, ...ACTION_CONFIG }
      : { params };

   const response = await axios.get('/users', config);
   const envelope = response?.data ?? {};

   return {
      users: envelope.data?.users ?? [],
      ...readPagination(envelope, perPage ?? 20),
   };
}

/**
 * Diretório mínimo para SELETOR de usuário fora da administração de IAM
 * (entrevistador do Recrutamento, responsável do RH, filtro de usuário dos
 * movimentos). Rota `GET /users/directory` — handoff 0110 §3.9, adendo de
 * 2026-09-15.
 *
 * **`loginRequired`-only, sem RBAC.** Foi a resposta do backend ao caveat que
 * abrimos em 2026-09-14: o grant de `iam:user:read` a papéis operacionais foi
 * REJEITADO (abriria e-mails, contas excluídas e `GET /users/:id`), e no lugar
 * veio este diretório — mesmo padrão do `GET /billing-dunnings/assignees` da
 * régua de cobrança. Nenhum picker depende mais de permissão.
 *
 * Devolve **só `{ id, nome, is_active }`**, ordenado por nome, sem paginação.
 *
 * ⚠️ **Guardrail (ratificado com o backend em 2026-09-15):** se um seletor
 * futuro precisar de e-mail, papel ou qualquer outro campo, **não** peça para
 * engordar o diretório — é rota sem permission por design, e campo a mais é
 * exposição a mais. O caminho é pedido novo ao backend, com revisão do que a
 * rota expõe.
 *
 * `status='all'` inclui ex-usuários (`is_active: false`): filtro histórico não
 * pode perder quem saiu da empresa (decisão do Caio). Exibir esses com o
 * sufixo de `formatUserLabel` (`utils/format.js`).
 *
 * @param {object} [params]
 * @param {'active'|'inactive'|'all'} [params.status='active'] valor inválido → 422
 * @returns {Promise<Array<{id: number, nome: string, is_active: boolean}>>}
 */
export async function listUserDirectory({ status = 'active' } = {}) {
   const response = await axios.get('/users/directory', {
      params: { status },
   });
   return response?.data?.data?.users ?? [];
}

/**
 * Detalhe. Traz `role.description` e `pdvs: [{ id, pdv_name }]` além do shape
 * da listagem. Responde normalmente para conta EXCLUÍDA (`is_active: false`,
 * `deleted_at` preenchido) — é assim que a ficha abre a partir do filtro
 * `status=inactive`.
 */
export async function getUser(userId) {
   const response = await axios.get(`/users/${userId}`);
   return response?.data?.data?.user ?? null;
}

/**
 * Criação. `role_id` é obrigatório (o fallback silencioso para "visualizador"
 * morreu no Gen3) e `password_confirm` faz parte do contrato.
 *
 * Erros que a tela trata por campo: 409 `email_in_use` (field `email`),
 * 404 `role_not_found`, 422 de validação.
 */
export async function createUser({
   nome,
   email,
   roleId,
   password,
   passwordConfirm,
}) {
   const response = await axios.post(
      '/users',
      {
         nome,
         email,
         role_id: Number(roleId),
         password,
         password_confirm: passwordConfirm,
      },
      ACTION_CONFIG
   );

   return {
      user: response?.data?.data?.user ?? null,
      message: response?.data?.message ?? 'Usuário criado com sucesso.',
   };
}

/**
 * Edição parcial: `nome`, `role_id` e senha. O e-mail é IMUTÁVEL (chave de
 * login, sem rota para alterar) — não vai no payload nem que venha no objeto.
 *
 * ⚠️ Trocar a senha revoga TODAS as sessões do alvo. Quando o alvo é o próprio
 * operador, a sessão que está usando a tela morre junto.
 */
export async function updateUser(
   userId,
   { nome, roleId, password, passwordConfirm }
) {
   const payload = {};
   if (nome !== undefined) payload.nome = nome;
   if (roleId !== undefined && roleId !== '') payload.role_id = Number(roleId);
   if (password) {
      payload.password = password;
      payload.password_confirm = passwordConfirm;
   }

   const response = await axios.put(`/users/${userId}`, payload, ACTION_CONFIG);

   return {
      user: response?.data?.data?.user ?? null,
      message: response?.data?.message ?? 'Usuário atualizado com sucesso.',
   };
}

/**
 * Exclusão (soft-delete com active-slot). Responde 204 sem body.
 *
 * Efeito: o acesso morre no request seguinte (token e refresh param de valer),
 * a conta sai de `status=active` e passa a aparecer em `inactive`, e o e-mail
 * fica LIVRE para um novo cadastro. **Não há restore** — decisão de produto.
 *
 * Erros: 422 `self_delete_forbidden` (excluir a própria conta), 404 para id
 * inexistente OU já excluído.
 */
export async function deleteUser(userId) {
   await axios.delete(`/users/${userId}`, ACTION_CONFIG);
}

/**
 * Substituição TOTAL do conjunto de PDVs operáveis (array vazio revoga todos).
 * Exige `iam:user:update`, a mesma chave da edição.
 */
export async function setUserPdvs(userId, pdvIds) {
   const response = await axios.put(
      `/users/${userId}/pdvs`,
      { pdv_ids: (pdvIds ?? []).map(Number) },
      ACTION_CONFIG
   );

   return {
      user: response?.data?.data?.user ?? null,
      message: response?.data?.message ?? 'PDVs atualizados com sucesso.',
   };
}

// ===========================================================================
// CONTA DO PRÓPRIO USUÁRIO (sem RBAC — é quem está logado)
// ===========================================================================

/**
 * PDVs que o usuário logado opera. Fonte do select de PDV das telas de
 * movimentação e da ORIGEM de uma transferência.
 *
 * PONTE de shape (handoff 0110): o Gen3 envelopou a rota em JSend e os mesmos
 * campos desceram um nível, para `data`. A leitura tolera as duas formas para
 * que o front possa subir antes do backend — do contrário, o instante do deploy
 * do backend seria o instante em que sete telas operacionais perdem o select.
 * Remover o fallback da raiz quando o backend estiver em produção.
 *
 * @returns {Promise<{userId: number|null, userName: string, pdvs: Array}>}
 */
export async function getMyPdvs() {
   const response = await axios.get('/users/me/pdvs');
   const body = response?.data;
   const data = body?.data ?? body ?? {};

   return {
      userId: data.user_id ?? null,
      userName: data.user_name ?? '',
      pdvs: data.pdvs ?? [],
   };
}

/**
 * Troca a senha do próprio usuário.
 *
 * ⚠️ Sucesso REVOGA TODAS AS SESSÕES, inclusive a corrente — o backend já
 * limpou o cookie `rt`. Quem chama precisa encerrar a sessão local e mandar o
 * usuário para o login; qualquer request seguinte com o token antigo devolve
 * 401 `session_invalid`.
 *
 * Erros (todos 422, com `field` — vão INLINE, nunca em toast):
 *   invalid_current_password → current_password
 *   business_rule            → new_password (nova == atual) | new_password_confirm
 *   min_length / max_length  → new_password (6–50)
 *   required                 → o campo faltante
 * Mais 429 `rate_limit` (10 tentativas / 10 min por IP) — sem retry automático.
 *
 * @returns {Promise<{ sessionsRevoked: boolean, message: string }>}
 */
export async function changeMyPassword({
   currentPassword,
   newPassword,
   newPasswordConfirm,
}) {
   const response = await axios.put('/users/me/password', {
      current_password: currentPassword,
      new_password: newPassword,
      new_password_confirm: newPasswordConfirm,
   });

   return {
      sessionsRevoked: Boolean(response?.data?.data?.sessions_revoked),
      message: response?.data?.message ?? '',
   };
}

/**
 * Atualiza o próprio perfil. SÓ o nome — e-mail é imutável (chave de login),
 * `role_id` seria escalada de privilégio e senha tem rota própria.
 *
 * NÃO revoga sessão: o usuário continua logado.
 * Erros: 422 `required` / `min_length` / `max_length`, campo `nome`.
 *
 * @returns {Promise<object>} usuário atualizado
 */
export async function updateMyProfile({ nome }) {
   const response = await axios.put('/users/me', { nome });
   return response?.data?.data?.user ?? null;
}
